import React, { useState, useCallback } from 'react';

const BTN = {
  fn:  'bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold',
  op:  'text-white font-bold hover:brightness-110',
  eq:  'text-white font-bold hover:brightness-110 col-span-1',
  num: 'bg-white hover:bg-slate-50 text-slate-800 font-medium border border-slate-200',
};

export default function TestCalculator() {
  const [display, setDisplay]   = useState('0');
  const [prev,    setPrev]      = useState(null);   // previous number
  const [op,      setOp]        = useState(null);   // pending operator
  const [fresh,   setFresh]     = useState(false);  // next digit replaces display

  const MAX = 12; // max digits shown

  const digit = useCallback((d) => {
    setDisplay(cur => {
      if (fresh || cur === '0') return String(d);
      if (cur.replace('-', '').length >= MAX) return cur;
      return cur + d;
    });
    setFresh(false);
  }, [fresh]);

  const decimal = useCallback(() => {
    setDisplay(cur => {
      const base = fresh ? '0' : cur;
      setFresh(false);
      return base.includes('.') ? base : base + '.';
    });
  }, [fresh]);

  const clear = () => {
    setDisplay('0'); setPrev(null); setOp(null); setFresh(false);
  };

  const backspace = () => {
    setDisplay(cur => {
      if (fresh) return '0';
      if (cur.length <= 1 || (cur.length === 2 && cur.startsWith('-'))) return '0';
      return cur.slice(0, -1);
    });
    setFresh(false);
  };

  const toggleSign = () => {
    setDisplay(cur => cur.startsWith('-') ? cur.slice(1) : '-' + cur);
  };

  const percent = () => {
    setDisplay(cur => String(parseFloat(cur) / 100));
    setFresh(true);
  };

  const applyOp = (a, operator, b) => {
    switch (operator) {
      case '+': return a + b;
      case '−': return a - b;
      case '×': return a * b;
      case '÷': return b === 0 ? 'Error' : a / b;
      default:  return b;
    }
  };

  const fmt = (n) => {
    if (n === 'Error') return 'Error';
    const s = String(parseFloat(n.toPrecision(10)));
    return s.length > MAX ? parseFloat(n.toPrecision(8)).toString() : s;
  };

  const operator = useCallback((nextOp) => {
    const cur = parseFloat(display);
    if (prev !== null && op && !fresh) {
      const result = applyOp(prev, op, cur);
      if (result === 'Error') { setDisplay('Error'); setPrev(null); setOp(null); setFresh(true); return; }
      const formatted = fmt(result);
      setDisplay(formatted);
      setPrev(result);
    } else {
      setPrev(cur);
    }
    setOp(nextOp);
    setFresh(true);
  }, [display, prev, op, fresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const equals = useCallback(() => {
    if (prev === null || !op) return;
    const cur = parseFloat(display);
    const result = applyOp(prev, op, cur);
    if (result === 'Error') { setDisplay('Error'); setPrev(null); setOp(null); setFresh(true); return; }
    const formatted = fmt(result);
    setDisplay(formatted);
    setPrev(null);
    setOp(null);
    setFresh(true);
  }, [display, prev, op]); // eslint-disable-line react-hooks/exhaustive-deps

  const buttons = [
    { label: 'C',  action: clear,          cls: BTN.fn },
    { label: '±',  action: toggleSign,     cls: BTN.fn },
    { label: '%',  action: percent,        cls: BTN.fn },
    { label: '÷',  action: () => operator('÷'), cls: BTN.op, color: '#1e73be', active: op === '÷' },
    { label: '7',  action: () => digit(7), cls: BTN.num },
    { label: '8',  action: () => digit(8), cls: BTN.num },
    { label: '9',  action: () => digit(9), cls: BTN.num },
    { label: '×',  action: () => operator('×'), cls: BTN.op, color: '#1e73be', active: op === '×' },
    { label: '4',  action: () => digit(4), cls: BTN.num },
    { label: '5',  action: () => digit(5), cls: BTN.num },
    { label: '6',  action: () => digit(6), cls: BTN.num },
    { label: '−',  action: () => operator('−'), cls: BTN.op, color: '#1e73be', active: op === '−' },
    { label: '1',  action: () => digit(1), cls: BTN.num },
    { label: '2',  action: () => digit(2), cls: BTN.num },
    { label: '3',  action: () => digit(3), cls: BTN.num },
    { label: '+',  action: () => operator('+'), cls: BTN.op, color: '#1e73be', active: op === '+' },
    { label: '⌫',  action: backspace,      cls: BTN.fn },
    { label: '0',  action: () => digit(0), cls: BTN.num },
    { label: '.',  action: decimal,        cls: BTN.num },
    { label: '=',  action: equals,         cls: BTN.eq, color: '#2ea690' },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 mt-3">
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-2">
        <div className="w-2 h-2 rounded-full bg-[#2ea690]" />
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Calculator</p>
      </div>

      {/* Display */}
      <div className="bg-slate-800 rounded-lg px-3 py-2 mb-2 text-right min-h-[52px] flex flex-col justify-end">
        {op && (
          <p className="text-slate-400 text-[11px] mb-0.5 font-mono">
            {prev} {op}
          </p>
        )}
        <p className={`font-mono font-bold text-white break-all leading-tight ${display.length > 9 ? 'text-base' : display.length > 6 ? 'text-xl' : 'text-2xl'}`}>
          {display}
        </p>
      </div>

      {/* Buttons */}
      <div className="grid grid-cols-4 gap-1">
        {buttons.map(({ label, action, cls, color, active }) => (
          <button
            key={label}
            onClick={action}
            style={color ? { backgroundColor: active ? '#0f3d78' : color } : undefined}
            className={`h-10 rounded-lg text-sm transition select-none ${cls} ${active ? 'ring-2 ring-white/30' : ''}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
